// Self-service personnel edits (backend core/personnel_self_edit.py): the labels
// for what a person may change on My details, and how a recorded change reads in
// the reviewer's box on PersonnelDialog. One list, so both pages name a field the
// same way.

import { DECLARATIONS } from "./constants";

// Registration-number formats, shared by PersonnelDialog and My details.
export const GMC_RE = /^A?\d{7}$/;      // 7 digits, optional 'A' prefix
export const NMC_RE = /^[0-9A-Z]{8}$/;  // 8 alphanumeric, caps
// PA for paramedics, PH for physiotherapists; either when neither is held.
export const hcpcRegex = (quals) => {
  const prefixes = [quals.includes("Paramedic") && "PA", quals.includes("Physiotherapist") && "PH"].filter(Boolean);
  return new RegExp(`^(${(prefixes.length ? prefixes : ["PA", "PH"]).join("|")})\\d{5,6}$`);
};
// Uppercase and strip all whitespace, as a registration number is typed.
export const regInput = (v) => v.toUpperCase().replace(/\s+/g, "");

export const CHANGE_FIELD_LABELS = {
  phone: "Phone",
  date_of_birth: "Date of birth",
  gmc_number: "GMC number",
  doctor_grade: "Grade",
  nmc_pin: "NMC PIN",
  hcpc_number: "HCPC number",
  independent_prescriber: "Independent prescriber",
  mrt_team: "MRT team",
  indemnity_provider: "Indemnity provider",
  indemnity_provider_other: "Indemnity provider name",
  indemnity_expiry: "Indemnity expiry",
  indemnity_certificate_id: "Indemnity certificate",
  cv_document_id: "CV",
  driving_licence_number: "Licence number",
  driving_licence_test_date: "Licence test date",
  driving_licence_expiry: "Licence expiry",
  driving_licence_front_id: "Licence photo (front)",
  driving_licence_back_id: "Licence photo (back)",
  address_line1: "Address line 1",
  address_line2: "Address line 2",
  town: "Town",
  county: "County",
  postcode: "Postcode",
  emergency_contact_name: "Emergency contact name",
  emergency_contact_phone: "Emergency contact phone",
  reference_name: "Reference name",
  reference_position: "Reference position",
  reference_email: "Reference email",
  clothing_size: "Clothing size",
  vehicle: "Vehicle",
  vehicle_registration: "Vehicle registration",
};

// A qualification certificate's file and expiry, which live on their own row
// rather than the record (CERTIFICATE_PREFIX / CERTIFICATE_EXPIRY_PREFIX in
// core/personnel_self_edit.py).
export const certificateField = (q) => `certificate:${q}`;
export const certificateExpiryField = (q) => `certificate_expiry:${q}`;

export const changeLabel = (field) => {
  const [kind, q] = field.split(/:(.*)/);
  if (kind === "certificate") return `${q} certificate`;
  if (kind === "certificate_expiry") return `${q} certificate expiry`;
  const declaration = DECLARATIONS.find(d => d.field === field);
  if (declaration) return `Declaration: ${declaration.title}`;
  return CHANGE_FIELD_LABELS[field] || field;
};

// Local date-only formatter (avoids the UTC off-by-one that Date(iso) causes for
// "YYYY-MM-DD" values).
export const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y) return "—";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric",
  });
};

export const fmtDateTime = (iso) => (iso
  ? new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  })
  : "—");

// One side of a recorded change as it should read.
export const changeValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return value.name || `Document #${value.id}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return fmtDate(value);
  // A timestamp — when a declaration was made.
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return `Made ${fmtDateTime(value)}`;
  return String(value);
};

// The fields any of these changes touched — what PersonnelDialog highlights.
export const changedFields = (changes) =>
  new Set(changes.flatMap(c => c.changes.map(x => x.field)));
